import type { BaseItemSchema } from "@memorang/types";
import { getItemAudioUri, getItemVideoUri } from "./content";
import { isIntroScreen } from "./intro-screens";
import {
	getResponseMicDelaySeconds,
	getResponsePrepTime,
	getResponseTimeLimit,
	getResponseTimesOut,
	getSummativeResponseConfig,
	isItemTimerStartAfterAudio,
	isItemTimerStartAfterVideo,
	responseIsSelfTimed,
} from "./response-timing";

export type ItemTimingPlan = {
	hasItemTimer: boolean;
	timeLimitSeconds?: number;
	timerWaitsForAudioCompletion: boolean;
	micDelaySecondsAfterAudio: number;
	prepSecondsBeforeTimer: number;
	itemUiDrivesOwnTimer: boolean;
	timesOutWhenExpired: boolean;
	autoSkipsOnTimeout: boolean;
};

export const deriveItemTimingPlan = ({
	item,
	isSummativeTest,
	inReviewMode,
}: {
	item?: BaseItemSchema;
	isSummativeTest?: boolean;
	inReviewMode?: boolean;
}): ItemTimingPlan => {
	const response = item?.response;
	const isActiveSummative = Boolean(isSummativeTest) && !inReviewMode;
	const summativeConfig = getSummativeResponseConfig(
		response,
		isActiveSummative,
	);
	const timeLimitSeconds = getResponseTimeLimit(response);

	const hasItemTimer =
		Boolean(
			summativeConfig?.overrideTestTimerWithItemTimer && timeLimitSeconds,
		) || Boolean(summativeConfig && isIntroScreen(item) && timeLimitSeconds);

	const hasTimeLimit = Boolean(timeLimitSeconds);
	const itemHasAudio = Boolean(getItemAudioUri(item?.content));
	const itemHasVideo = Boolean(getItemVideoUri(item?.content));
	const hasPrepTimeConfig = response?.config?.prepTime != null;
	const timerStartsAfterStimulus =
		((isItemTimerStartAfterAudio(response?.config) && itemHasAudio) ||
			(isItemTimerStartAfterVideo(response?.config) && itemHasVideo)) &&
		hasPrepTimeConfig;
	const timerWaitsForAudioCompletion = hasTimeLimit && timerStartsAfterStimulus;

	const micDelaySecondsAfterAudio = timerWaitsForAudioCompletion
		? getResponseMicDelaySeconds(response)
		: 0;

	const prepSecondsBeforeTimer =
		hasTimeLimit && !timerStartsAfterStimulus
			? (getResponsePrepTime(response) ?? 0)
			: 0;

	const itemUiDrivesOwnTimer =
		!isIntroScreen(item) && responseIsSelfTimed(response);
	return {
		hasItemTimer,
		timeLimitSeconds,
		timerWaitsForAudioCompletion,
		micDelaySecondsAfterAudio,
		prepSecondsBeforeTimer,
		itemUiDrivesOwnTimer,
		timesOutWhenExpired: getResponseTimesOut(response, isActiveSummative),
		autoSkipsOnTimeout: Boolean(summativeConfig?.autoSkipOnTimeout),
	};
};
